import { useState } from 'react';
import {
  AlertTriangle, CheckCircle, Eye, Lock, Phone, Mail,
  Banknote, MapPinOff, Timer, TrendingDown, Megaphone,
} from 'lucide-react';
import { Footer } from '@/sections/Footer';
import { SEO } from '@/components/SEO';
import { FaqSection, faqSchema } from '@/components/FaqSection';
import { useReveal } from '@/lib/useReveal';
import { Section, Container, Display, Lead, Tile } from '@/components/system';
import { submitForm } from '@/lib/forms';
import { FRAUD_ALERTS } from '@/data/fraud_alerts';

const faqs = [
  {
    question: '¿Cómo identifico una agencia de envíos fraudulenta?',
    answer:
      'Desconfía de precios demasiado bajos para ser verdad, de quienes solo aceptan pago en efectivo sin recibo, de negocios sin dirección física verificable y de quienes te presionan para decidir de inmediato.',
  },
  {
    question: '¿Qué hago si sospecho de un fraude?',
    answer:
      'No entregues dinero ni paquetes. Repórtalo con el formulario de esta página, escribe a fraude@mienviocuba.com o llama al +1 (786) 519-1709.',
  },
  {
    question: '¿Cómo protege Mi Envío Cuba a los clientes?',
    answer:
      'Las agencias que operan con MEC pasan un proceso de verificación, cada entrega se confirma con foto, firma del receptor y GPS, los datos personales se guardan cifrados y toda la actividad queda registrada en una auditoría inalterable.',
  },
  {
    question: '¿Las agencias que usan MEC están verificadas?',
    answer:
      'Las agencias pasan un proceso de verificación que incluye documentación legal vigente y una ubicación física verificable, y su actividad dentro del sistema queda auditada.',
  },
];

const warningSigns = [
  { icon: TrendingDown, title: 'Precios sospechosamente bajos.', text: 'Si un precio parece demasiado bueno para ser verdad, probablemente lo sea.' },
  { icon: Banknote, title: 'Pago solo en efectivo.', text: 'Desconfía de quienes solo aceptan pagos en efectivo sin recibo.' },
  { icon: MapPinOff, title: 'Sin dirección física.', text: 'Las agencias legítimas tienen una ubicación física verificable.' },
  { icon: Timer, title: 'Presión para decidir rápido.', text: 'Ten cuidado con quienes te presionan para tomar una decisión inmediata.' },
];

const protections = [
  { icon: Eye, title: 'Agencias verificadas.', text: 'Documentación legal y ubicación física verificable antes de operar en la plataforma.' },
  { icon: Lock, title: 'Datos cifrados.', text: 'Tu información viaja y se guarda cifrada, y toda la actividad queda en una auditoría inalterable.' },
  { icon: CheckCircle, title: 'Prueba de entrega verificada.', text: 'Foto, firma del receptor y GPS en cada entrega — la evidencia protege al cliente y a la agencia.' },
];

function ReportForm() {
  const [form, setForm] = useState({ nombre: '', email: '', telefono: '', empresa: '', canal: '', descripcion: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await submitForm('fraude', form);
      setStatus('success');
    } catch (err) {
      console.error('[AntiFraude] report submission failed:', err);
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="text-center py-14">
        <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
        <h3 className="font-heading text-2xl font-semibold text-text-primary mb-2">Reporte recibido.</h3>
        <p className="text-text-secondary max-w-md mx-auto">
          Gracias por avisar. Nuestro equipo lo revisará y, si dejaste un correo, te contactamos
          si necesitamos más detalles.
        </p>
      </div>
    );
  }

  const input =
    'w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand bg-white';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Tu nombre (opcional)</label>
          <input name="nombre" value={form.nombre} onChange={handleChange} className={input} placeholder="Puedes reportar de forma anónima" />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Tu correo (opcional)</label>
          <input name="email" type="email" value={form.email} onChange={handleChange} className={input} placeholder="Solo si quieres seguimiento" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">Negocio o persona que reportas *</label>
          <input name="empresa" required value={form.empresa} onChange={handleChange} className={input} placeholder="Nombre del negocio, perfil o número" />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">¿Dónde ocurrió?</label>
          <select name="canal" value={form.canal} onChange={handleChange} className={input}>
            <option value="">Seleccionar...</option>
            <option value="Facebook / Instagram">Facebook / Instagram</option>
            <option value="WhatsApp / SMS">WhatsApp / SMS</option>
            <option value="Llamada telefónica">Llamada telefónica</option>
            <option value="Local físico">Local físico</option>
            <option value="Sitio web">Sitio web</option>
            <option value="Otro">Otro</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-text-primary mb-1">¿Qué pasó? *</label>
        <textarea
          name="descripcion" required value={form.descripcion} onChange={handleChange} rows={5} className={input}
          placeholder="Cuenta lo que pasó: qué te ofrecieron, qué te pidieron, montos, números de teléfono o enlaces involucrados…"
        />
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-4">
        <button
          type="submit"
          disabled={status === 'loading'}
          className="inline-flex items-center justify-center rounded-full bg-brand text-white font-medium px-7 h-12 hover:bg-brand-deep transition-colors disabled:opacity-60 w-full sm:w-auto"
        >
          {status === 'loading' ? 'Enviando…' : 'Enviar reporte'}
        </button>
        {status === 'error' && (
          <p className="text-sm text-red-600">No se pudo enviar. Intenta de nuevo o escribe a fraude@mienviocuba.com.</p>
        )}
      </div>
      <p className="text-xs text-text-tertiary">
        Tu reporte se trata de forma confidencial. No publicamos nombres sin verificación.
      </p>
    </form>
  );
}

export function AntiFraude() {
  const heroRef = useReveal<HTMLElement>();
  const signsRef = useReveal<HTMLElement>();
  const alertsRef = useReveal<HTMLElement>();
  const protRef = useReveal<HTMLElement>();
  const reportRef = useReveal<HTMLElement>();

  return (
    <main className="bg-bg-light">
      <SEO
        title="Anti-Fraude | Protégete de Estafas en Envíos a Cuba"
        description="Aprende a identificar estafas en envíos a Cuba, revisa las alertas de fraude activas y reporta negocios fraudulentos. Cómo la plataforma MEC protege a clientes y agencias."
        path="/anti-fraude"
        keywords="fraude envíos Cuba, estafas agencias de envíos, reportar estafa envíos Cuba, alertas de fraude Cuba, agencias verificadas"
        jsonLd={faqSchema(faqs)}
      />

      {/* Hero */}
      <Section tone="white" ref={heroRef} className="pt-[120px] sm:pt-[150px] pb-16 sm:pb-20">
        <Container>
          <div className="mx-auto max-w-4xl text-center">
            <p data-reveal className="text-brand font-semibold text-base sm:text-lg mb-4">Anti-fraude</p>
            <Display data-reveal as="h1" size="xl">La confianza no se promete. Se demuestra.</Display>
            <Lead data-reveal className="mt-6 mx-auto max-w-2xl">
              Aprende a reconocer las estafas del corredor a Cuba, revisa las alertas activas
              y reporta lo que veas — protege a tu familia y a las agencias serias.
            </Lead>
            <div data-reveal className="mt-8">
              <a
                href="#reportar"
                className="inline-flex items-center justify-center rounded-full bg-brand text-white font-medium px-7 h-12 text-[17px] hover:bg-brand-deep transition-colors"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                Reportar un fraude
              </a>
            </div>
          </div>
        </Container>
      </Section>

      {/* Warning signs */}
      <Section tone="grey" ref={signsRef} className="py-16 sm:py-20">
        <Container>
          <Display data-reveal size="md" className="mb-10">Señales de alerta.</Display>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {warningSigns.map((w) => (
              <Tile key={w.title} data-reveal className="p-7">
                <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
                  <w.icon className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-semibold tracking-tight">{w.title}</h3>
                <p className="mt-2 text-text-secondary text-sm leading-relaxed">{w.text}</p>
              </Tile>
            ))}
          </div>
        </Container>
      </Section>

      {/* Active alerts / known scam patterns */}
      <Section tone="white" ref={alertsRef} className="py-16 sm:py-20">
        <Container>
          <div className="max-w-3xl">
            <Display data-reveal size="md">Alertas de fraude.</Display>
            <Lead data-reveal className="mt-4">
              Modalidades de estafa conocidas en el corredor a Cuba. Publicaremos aquí las
              alertas activas que nuestro equipo verifique.
            </Lead>
          </div>
          <div className="mt-10 space-y-4">
            {FRAUD_ALERTS.map((a) => (
              <article key={a.title} data-reveal className="rounded-[24px] bg-bg-light p-6 sm:p-8 flex gap-5 items-start">
                <div className="w-11 h-11 shrink-0 rounded-xl bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-heading text-lg font-semibold tracking-tight">{a.title}</h3>
                    <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-0.5">
                      {a.kind === 'alerta' ? (a.date ? `Alerta · ${a.date}` : 'Alerta activa') : 'Modalidad conocida'}
                    </span>
                  </div>
                  <p className="mt-2 text-text-secondary leading-relaxed">{a.description}</p>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </Section>

      {/* How MEC protects */}
      <Section tone="grey" ref={protRef} className="py-16 sm:py-20">
        <Container>
          <Display data-reveal size="md" className="mb-10">Cómo te protege la plataforma.</Display>
          <div className="grid sm:grid-cols-3 gap-4">
            {protections.map((p) => (
              <Tile key={p.title} data-reveal className="p-7 sm:p-8">
                <div className="w-11 h-11 rounded-xl bg-brand/10 flex items-center justify-center">
                  <p.icon className="w-5 h-5 text-brand" />
                </div>
                <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">{p.title}</h3>
                <p className="mt-2 text-text-secondary leading-relaxed">{p.text}</p>
              </Tile>
            ))}
          </div>
        </Container>
      </Section>

      {/* Report form */}
      <Section tone="white" ref={reportRef} id="reportar" className="py-16 sm:py-20 scroll-mt-16">
        <Container>
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10 lg:gap-16 items-start">
            <div>
              <Display data-reveal size="md">Reporta un fraude.</Display>
              <Lead data-reveal className="mt-4">
                ¿Un negocio sospechoso, una estafa que recibiste, un perfil falso? Cuéntanos.
                Cada reporte ayuda a proteger a la comunidad.
              </Lead>
              <div data-reveal className="mt-8 space-y-3 text-text-secondary">
                <a href="mailto:fraude@mienviocuba.com" className="flex items-center gap-3 hover:text-text-primary transition-colors">
                  <Mail className="w-5 h-5 text-brand" /> fraude@mienviocuba.com
                </a>
                <a href="tel:+17865191709" className="flex items-center gap-3 hover:text-text-primary transition-colors">
                  <Phone className="w-5 h-5 text-brand" /> +1 (786) 519-1709
                </a>
              </div>
            </div>
            <div data-reveal className="rounded-[28px] bg-bg-light p-7 sm:p-9">
              <ReportForm />
            </div>
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section tone="grey" className="py-16 sm:py-20 pb-0">
        <FaqSection faqs={faqs} />
      </Section>

      <Footer />
    </main>
  );
}
